import React, { useEffect, useState, useCallback } from "react";
import { InfoTip } from "@/components/InfoTip";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2,
  ShieldCheck,
  UserPlus,
  UserMinus,
  RefreshCw,
  Sparkles,
  Activity,
  CheckCircle2,
  XCircle,
  KeyRound,
  TrendingUp,
  Users,
  CreditCard,
  BarChart3,
  DollarSign,
  Flame,
} from "lucide-react";
import { api } from "@/lib/api";
import { AdminGrantCredits } from "@/components/AdminGrantCredits";
import { AdminUnlimited } from "@/components/AdminUnlimited";
import { AdminFreeTrial } from "@/components/AdminFreeTrial";
import { AdminAiLimit } from "@/components/AdminAiLimit";
import { AdminOrgPools } from "@/components/AdminOrgPools";
import { AdminSales } from "@/components/AdminSales";
import { AdminDocuments } from "@/components/AdminDocuments";
import { AdminQuote } from "@/components/AdminQuote";
import { AdminLedger } from "@/components/AdminLedger";
import { AdminProfit } from "@/components/AdminProfit";
import { AdminLegalInfo } from "@/components/AdminLegalInfo";
import { AdminSchoolOffer } from "@/components/AdminSchoolOffer";
import { useAuth } from "@/contexts/AuthContext";

function formatRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diffMs = Date.now() - d.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return "à l'instant";
  const min = Math.floor(sec / 60);
  if (min < 60) return `il y a ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `il y a ${h}h`;
  const j = Math.floor(h / 24);
  if (j < 30) return `il y a ${j}j`;
  return d.toLocaleDateString("fr-FR", { year: "numeric", month: "short", day: "2-digit" });
}

// ---------- Dashboard sub-components (mobile-first) ----------
function StatCard({ icon, label, value, sub, accent = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border ${accent ? "border-[#FF2A00] bg-[#FF2A00]/5" : "border-border"} p-4`}
    >
      <div className="font-mono uppercase tracking-[0.14em] text-[9px] text-muted-foreground flex items-center gap-1 mb-2">
        {icon}
        {label}
      </div>
      <div className={`text-2xl font-black ${accent ? "text-[#FF2A00]" : ""}`} style={{ fontFamily: "Outfit" }}>
        {value}
      </div>
      {sub && <div className="font-mono text-[10px] text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

function BurnCard({ label, value, warn = false, testid }) {
  return (
    <div
      data-testid={testid}
      className={`border-l-4 ${warn ? "border-l-[#FF2A00]" : "border-l-[#002FA7]"} border border-border p-4`}
    >
      <div className="font-mono uppercase tracking-[0.14em] text-[9px] text-muted-foreground mb-2">
        {label}
      </div>
      <div className="text-xl font-black" style={{ fontFamily: "Outfit" }}>
        {value}
      </div>
    </div>
  );
}

// Weekly funnel: where visitors stop between first visit and payment.
function WeeklyFunnel() {
  const [data, setData] = useState(null);
  const [idx, setIdx] = useState(0);
  useEffect(() => { api.get("/admin/funnel-weekly").then(({ data: d }) => setData(d.weeks)).catch(() => setData([])); }, []);
  if (!data) return null;
  if (!data.length) return <div className="text-sm text-muted-foreground">Données indisponibles.</div>;
  const steps = data[idx].steps;
  let worst = null;
  steps.forEach((s, i) => {
    if (!i || !steps[i - 1].count) return;
    const loss = (steps[i - 1].count - s.count) / steps[i - 1].count;
    if (loss > 0 && (!worst || loss > worst.loss)) worst = { loss, from: steps[i - 1].step, to: s.step };
  });
  const label = (w) => new Date(`${w}T00:00:00`).toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
  return (
    <div data-testid="admin-weekly-funnel">
      <div className="flex flex-wrap gap-1.5 mb-4">
        {data.map((w, i) => (
          <button key={w.week} type="button" onClick={() => setIdx(i)} data-testid={`weekly-funnel-week-${i}`}
            className={`font-mono text-[10px] uppercase tracking-[0.1em] px-2.5 py-1.5 border transition-colors ${i === idx ? "bg-[#002FA7] text-white border-[#002FA7]" : "border-border hover:border-[#002FA7]"}`}>
            {i === 0 ? "Cette semaine" : `Sem. du ${label(w.week)}`}
          </button>
        ))}
      </div>
      <FunnelRows steps={steps} />
      <p className="mt-3 text-sm" data-testid="weekly-funnel-example-views">
        Ont ouvert « Essayer avec un exemple » : <b>{data[idx].example_views ?? 0}</b> visiteur(s) (0 € de coût IA).
        <br /><span data-testid="weekly-funnel-dys-views">Ont visité la page parents DYS (sux1.app/dys) : <b>{data[idx].dys_views ?? 0}</b> visiteur(s).</span>
      </p>
      {worst && (
        <p className="mt-4 text-sm" data-testid="weekly-funnel-worst">
          Plus grosse perte : <b>{worst.from}</b> → <b>{worst.to}</b> (−{Math.round(worst.loss * 100)} %). C'est l'étape à améliorer en priorité.
        </p>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">Chaque visiteur n'est compté qu'une fois par étape et par semaine. Suivi démarré le jour de la mise en ligne de ce tableau.</p>
    </div>
  );
}

function FunnelRows({ steps }) {
  const top = Math.max(1, ...steps.map((s) => s.count));
  return (
    <div className="space-y-2">
      {steps.map((s, i) => {
        const pct = Math.round((s.count / top) * 100);
        const prev = i > 0 ? steps[i - 1].count : null;
        const dropPct = prev && prev > 0 ? Math.round(((prev - s.count) / prev) * 100) : null;
        return (
          <div key={s.step} data-testid={`funnel-step-${i}`} className="flex items-center gap-3">
            <div className="w-40 shrink-0 font-mono text-[11px] uppercase tracking-[0.1em]">
              {s.step}
            </div>
            <div className="flex-1 h-6 bg-secondary/40 relative overflow-hidden">
              <div
                className="h-full bg-[#002FA7]"
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="w-10 shrink-0 font-mono text-sm font-black text-right" data-testid={`funnel-step-${i}-count`}>
              {s.count}
            </div>
            <div className="w-16 shrink-0 font-mono text-[10px] text-right">
              {dropPct !== null && dropPct > 0 ? (
                <span className="text-[#FF2A00]">-{dropPct}%</span>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function MiniChart({ title, series, color, testid }) {
  const max = Math.max(1, ...series.map((s) => s.count));
  const total = series.reduce((s, x) => s + x.count, 0);
  return (
    <div className="border border-border p-4" data-testid={testid}>
      <div className="flex items-baseline justify-between mb-3">
        <div className="sux-marker">{title}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{total} total</div>
      </div>
      <div className="flex items-end gap-[2px] h-24">
        {series.map((s) => (
          <div
            key={s.date}
            className="flex-1 relative group"
            title={`${s.date} — ${s.count}`}
          >
            <div
              className="w-full"
              style={{
                height: `${Math.max(2, (s.count / max) * 100)}%`,
                backgroundColor: s.count === 0 ? "#e5e5e5" : color,
                opacity: s.count === 0 ? 0.3 : 1,
              }}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between font-mono text-[9px] text-muted-foreground mt-2">
        <span>{series[0]?.date?.slice(5)}</span>
        <span>{series[series.length - 1]?.date?.slice(5)}</span>
      </div>
    </div>
  );
}

function ModelSplit({ rows }) {
  const total = rows.reduce((s, r) => s + r.count, 0) || 1;
  const colors = { "claude-sonnet-4-5": "#8B5CF6", "gpt-5.2": "#10B981", "gemini-3-flash": "#F59E0B" };
  return (
    <div className="space-y-2">
      {rows.map((r) => {
        const pct = Math.round((r.count / total) * 100);
        return (
          <div key={r.model} className="flex items-center gap-3" data-testid={`model-row-${r.model}`}>
            <div className="w-32 shrink-0 font-mono text-[10px] uppercase tracking-[0.1em] truncate">
              {r.model}
            </div>
            <div className="flex-1 h-4 bg-secondary/40 relative overflow-hidden">
              <div
                className="h-full"
                style={{ width: `${pct}%`, backgroundColor: colors[r.model] || "#666" }}
              />
            </div>
            <div className="w-20 shrink-0 font-mono text-[10px] text-right">
              {r.count} · {pct}%
            </div>
          </div>
        );
      })}
      {rows.length === 0 && (
        <div className="font-mono text-[10px] text-muted-foreground">Aucun résumé sur cette période.</div>
      )}
    </div>
  );
}

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const [checking, setChecking] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [proUsers, setProUsers] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [activity, setActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [marketing, setMarketing] = useState(null);
  const [marketingLoading, setMarketingLoading] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(false);

  const loadProUsers = useCallback(async () => {
    setListLoading(true);
    try {
      const { data } = await api.get("/admin/pro-users");
      setProUsers(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load Pro users.");
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadActivity = useCallback(async () => {
    setActivityLoading(true);
    try {
      const { data } = await api.get("/admin/users-activity");
      setActivity(data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du chargement de l'activité.");
    } finally {
      setActivityLoading(false);
    }
  }, []);

  const loadMarketing = useCallback(async () => {
    setMarketingLoading(true);
    try {
      const { data } = await api.get("/admin/marketing");
      setMarketing(data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du chargement des données marketing.");
    } finally {
      setMarketingLoading(false);
    }
  }, []);

  const loadDashboard = useCallback(async () => {
    setDashboardLoading(true);
    try {
      const { data } = await api.get("/admin/dashboard");
      setDashboard(data || null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec du chargement du dashboard.");
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setChecking(false);
      return;
    }
    api
      .get("/admin/me")
      .then((res) => {
        const ok = !!res.data?.is_admin;
        setIsAdmin(ok);
        if (ok) {
          loadProUsers();
          loadActivity();
          loadMarketing();
          loadDashboard();
        }
      })
      .catch(() => setIsAdmin(false))
      .finally(() => setChecking(false));
  }, [authLoading, user, loadProUsers, loadActivity, loadMarketing, loadDashboard]);

  if (authLoading || checking) {
    return (
      <div className="p-12 flex items-center gap-3 font-mono text-sm text-muted-foreground" data-testid="admin-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading admin panel…
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  if (!isAdmin) {
    return (
      <div className="max-w-xl mx-auto p-12 mt-12 border border-[#FF2A00] bg-[#FF2A00]/5" data-testid="admin-forbidden">
        <div className="sux-marker mb-3 text-[#FF2A00]">403 · Forbidden</div>
        <h2 className="text-2xl font-black mb-3" style={{ fontFamily: "Outfit" }}>
          Cette zone est réservée à l&apos;admin.
        </h2>
        <p className="font-mono text-xs text-muted-foreground break-words">
          Connecté en tant que : {user.email}
        </p>
      </div>
    );
  }

  const handleRevoke = async (targetEmail) => {
    if (!window.confirm(`Révoquer l'accès Pro de ${targetEmail} ?`)) return;
    try {
      const { data } = await api.post("/admin/revoke-pro", { email: targetEmail });
      toast.success(data?.message || `Pro révoqué pour ${targetEmail}.`);
      loadProUsers();
      loadActivity();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la révocation.");
    }
  };

  const handleResetPassword = async (targetEmail) => {
    const newPwd = window.prompt(
      `Nouveau mot de passe pour ${targetEmail} (6 caractères minimum) :`,
      "Sux1-" + Math.random().toString(36).slice(2, 8)
    );
    if (!newPwd) return;
    if (newPwd.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères.");
      return;
    }
    try {
      await api.post("/admin/reset-password", { email: targetEmail, new_password: newPwd });
      try {
        await navigator.clipboard.writeText(newPwd);
        toast.success(`Mot de passe réinitialisé pour ${targetEmail} — copié dans le presse-papier.`, { duration: 8000 });
      } catch {
        toast.success(`Mot de passe réinitialisé pour ${targetEmail} : ${newPwd}`, { duration: 10000 });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Échec de la réinitialisation.");
    }
  };

  // Stats
  const totalUsers = activity.length;
  const activeUsers = activity.filter((u) => (u.summaries_count || 0) > 0).length;
  const proCount = activity.filter((u) => u.is_pro).length;
  const inactiveUsers = activity.filter((u) => (u.summaries_count || 0) === 0).length;

  return (
    <div className="border-t border-border" data-testid="admin-panel">
      <div className="grid grid-cols-1 md:grid-cols-12">
        <aside className="md:col-span-3 border-r border-b border-border p-8">
          <div className="sux-marker mb-3 flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> Admin
          </div>
          <h2 className="text-2xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
            Pass Pro à vie
          </h2>
          <p className="text-muted-foreground text-sm mt-3">
            Offrez des crédits à vos formateurs et testeurs (coût fixe et limité). Le bénéficiaire doit d&apos;abord créer son compte SUX1.
          </p>
          <div className="mt-6 font-mono text-[11px] text-muted-foreground border-t border-border pt-4">
            Connecté : {user.email}
          </div>
          <a
            href="/kit-marketing.html"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="admin-kit-marketing-link"
            className="mt-6 inline-flex items-center gap-2 font-mono uppercase tracking-[0.14em] text-[10px] text-[#002FA7] hover:text-[#00227A] border-t border-border pt-4"
            title="Ouvrir votre kit marketing privé (posts LinkedIn, PDF enseignant, emails INSPÉ)"
          >
            <Sparkles className="w-3 h-3" /> Kit marketing →
          </a>
        </aside>

        <section className="md:col-span-9 p-8 lg:p-12 border-b border-border">
          {/* -------------------- MOBILE-FIRST DASHBOARD -------------------- */}
          <div className="mb-10" data-testid="admin-dashboard-panel">
            <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
              <div>
                <div className="sux-marker mb-2 flex items-center gap-2 text-[#002FA7]">
                  <BarChart3 className="w-3 h-3" /> Dashboard <InfoTip testid="admin-dashboard-info" content={"Vue rapide de l'activité : inscriptions, résumés, achats. Cliquez sur Actualiser pour les derniers chiffres."} />
                </div>
                <h3 className="text-2xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
                  Vue d&apos;ensemble
                </h3>
              </div>
              <button
                type="button"
                onClick={loadDashboard}
                disabled={dashboardLoading}
                data-testid="admin-dashboard-refresh"
                className="font-mono uppercase tracking-[0.14em] text-[11px] px-4 py-2 border border-border hover:bg-[#FF2A00] hover:text-white hover:border-[#002FA7] transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${dashboardLoading ? "animate-spin" : ""}`} /> Actualiser
              </button>
            </div>

            {dashboardLoading && !dashboard && (
              <div className="border border-dashed border-border p-8 text-center font-mono text-xs text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Chargement…
              </div>
            )}

            {dashboard && (
              <>
                {/* Headline stat cards — 2 cols mobile, 4 cols desktop */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6" data-testid="admin-dashboard-totals">
                  <StatCard
                    icon={<Users className="w-3 h-3" />}
                    label="Inscrits"
                    value={dashboard.totals.users.toLocaleString("fr-FR")}
                    testid="stat-users"
                  />
                  <StatCard
                    icon={<CheckCircle2 className="w-3 h-3" />}
                    label="Pro"
                    value={dashboard.totals.pro_users.toLocaleString("fr-FR")}
                    sub={`${(dashboard.totals.conversion_rate * 100).toFixed(1)} %`}
                    testid="stat-pro"
                  />
                  <StatCard
                    icon={<Activity className="w-3 h-3" />}
                    label="Résumés"
                    value={dashboard.totals.summaries.toLocaleString("fr-FR")}
                    testid="stat-summaries"
                  />
                  <StatCard
                    icon={<DollarSign className="w-3 h-3" />}
                    label="Revenu Pro"
                    value={`€${dashboard.burn.revenue_pro_users_eur.toFixed(0)}`}
                    testid="stat-revenue"
                    accent
                  />
                </div>

                {/* Burn rate */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-8" data-testid="admin-dashboard-burn">
                  <BurnCard
                    label="Coût 24h"
                    value={`$${dashboard.burn.last_24h_usd.toFixed(2)}`}
                    testid="burn-24h"
                  />
                  <BurnCard
                    label="Coût 30j"
                    value={`$${dashboard.burn.last_30d_usd.toFixed(2)}`}
                    testid="burn-30d"
                  />
                  <BurnCard
                    label="Projection mensuelle"
                    value={`$${dashboard.burn.monthly_projection_usd.toFixed(2)}`}
                    testid="burn-projection"
                    warn
                  />
                </div>

                <div className="border-2 border-[#002FA7] p-5 mb-6" data-testid="admin-weekly-funnel-card">
                  <div className="sux-marker mb-3 flex items-center gap-2">
                    <TrendingUp className="w-3 h-3" /> Où les gens s'arrêtent (par semaine) <InfoTip testid="admin-weekly-funnel-info" content={"Pour chaque semaine : combien de visiteurs passent chaque étape (visite, essai, inscription, achat). La plus grosse chute montre ce qu'il faut améliorer."} />
                  </div>
                  <WeeklyFunnel />
                </div>

                {/* Funnel */}
                <div className="border border-border p-5 mb-6" data-testid="admin-dashboard-funnel">
                  <div className="sux-marker mb-3 flex items-center gap-2">
                    <TrendingUp className="w-3 h-3" /> Funnel de conversion <InfoTip testid="admin-funnel-info" content={"Le parcours complet depuis le début : chaque ligne montre combien de personnes atteignent l'étape, et le pourcentage gardé par rapport à l'étape précédente."} />
                  </div>
                  <FunnelRows steps={dashboard.funnel} />
                </div>

                {/* Signups + Summaries per day */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                  <MiniChart
                    title="Inscriptions 30j"
                    series={dashboard.signups_last_30d}
                    color="#002FA7"
                    testid="chart-signups"
                  />
                  <MiniChart
                    title="Résumés 30j"
                    series={dashboard.summaries_last_30d}
                    color="#FF2A00"
                    testid="chart-summaries"
                  />
                </div>

                {/* Model split */}
                <div className="border border-border p-5" data-testid="admin-dashboard-models">
                  <div className="sux-marker mb-3 flex items-center gap-2">
                    <Flame className="w-3 h-3" /> Modèles LLM utilisés (30j) <InfoTip testid="admin-models-info" content={"Quelle IA vos clients choisissent. Gemini 3 Flash coûte le moins cher ; Claude et GPT coûtent plus et sont facturés 2 crédits."} />
                  </div>
                  <ModelSplit rows={dashboard.model_split_last_30d} />
                </div>
              </>
            )}
          </div>

          {/* -------------------- END DASHBOARD -------------------- */}
          <AdminDocuments />
          <AdminSales />
          <AdminProfit />
          <AdminLedger />
          <AdminLegalInfo />
          <div className="border-2 border-[#002FA7] p-5 my-6" data-testid="admin-school-offer-card">
            <div className="sux-marker mb-3 flex items-center gap-2">Offre établissement (devis, virement, carte) <InfoTip testid="admin-school-offer-info" content={"Cochez pour ouvrir la page de devis aux écoles (SIRET obligatoire). Ajoutez votre IBAN pour les virements. Quand un virement arrive, cliquez « Payé » : les crédits et la facture partent tout seuls."} /></div>
            <AdminSchoolOffer />
          </div>
          <AdminAiLimit />
          <AdminQuote />
          <AdminOrgPools />
          <AdminGrantCredits />
          <AdminUnlimited />
          <AdminFreeTrial />

          {/* Stats */}
          <div className="mt-10 max-w-5xl grid grid-cols-2 md:grid-cols-4 gap-0 border border-border" data-testid="admin-stats">
            <div className="p-5 border-r border-b md:border-b-0 border-border">
              <div className="sux-marker mb-1">Total</div>
              <div className="text-2xl font-black" style={{ fontFamily: "Outfit" }}>{totalUsers}</div>
              <div className="text-[11px] text-muted-foreground mt-1">inscrits</div>
            </div>
            <div className="p-5 md:border-r border-b md:border-b-0 border-border">
              <div className="sux-marker mb-1">Actifs</div>
              <div className="text-2xl font-black" style={{ fontFamily: "Outfit" }}>{activeUsers}</div>
              <div className="text-[11px] text-muted-foreground mt-1">≥ 1 résumé</div>
            </div>
            <div className="p-5 border-r border-border">
              <div className="sux-marker mb-1">Pro</div>
              <div className="text-2xl font-black text-[#002FA7]" style={{ fontFamily: "Outfit" }}>{proCount}</div>
              <div className="text-[11px] text-muted-foreground mt-1">comptes Pro</div>
            </div>
            <div className="p-5">
              <div className="sux-marker mb-1 text-[#FF2A00]">Inactifs</div>
              <div className="text-2xl font-black text-[#FF2A00]" style={{ fontFamily: "Outfit" }}>{inactiveUsers}</div>
              <div className="text-[11px] text-muted-foreground mt-1">0 résumé</div>
            </div>
          </div>

          {/* Activity table */}
          <div className="mt-10 max-w-5xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="sux-marker mb-1 flex items-center gap-2">
                  <Activity className="w-3 h-3" /> Activité des testeurs
                </div>
                <h3 className="text-xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
                  Tous les utilisateurs
                </h3>
              </div>
              <button
                type="button"
                onClick={() => { loadActivity(); loadProUsers(); loadMarketing(); }}
                disabled={activityLoading}
                data-testid="admin-activity-refresh-btn"
                className="font-mono uppercase tracking-[0.2em] text-[10px] border border-border px-3 py-2 hover:bg-[#FF2A00] hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {activityLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Rafraîchir
              </button>
            </div>

            <div className="border border-border overflow-x-auto" data-testid="admin-activity-table">
              <table className="w-full text-sm">
                <thead className="bg-[#002FA7]/5 border-b border-border">
                  <tr>
                    <th className="text-left p-3 font-mono uppercase tracking-[0.15em] text-[10px]">Email</th>
                    <th className="text-left p-3 font-mono uppercase tracking-[0.15em] text-[10px] hidden md:table-cell">Inscrit</th>
                    <th className="text-right p-3 font-mono uppercase tracking-[0.15em] text-[10px]">Résumés</th>
                    <th className="text-left p-3 font-mono uppercase tracking-[0.15em] text-[10px] hidden md:table-cell">Dernière act.</th>
                    <th className="text-center p-3 font-mono uppercase tracking-[0.15em] text-[10px]">Pro</th>
                    <th className="text-right p-3 font-mono uppercase tracking-[0.15em] text-[10px]">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {activity.length === 0 && !activityLoading && (
                    <tr>
                      <td colSpan={6} className="p-6 text-center text-muted-foreground font-mono text-sm">
                        Aucun utilisateur pour le moment.
                      </td>
                    </tr>
                  )}
                  {activity.map((u) => {
                    const isSelf = u.email.toLowerCase() === user.email.toLowerCase();
                    return (
                      <tr key={u.email} className="border-t border-border" data-testid={`admin-activity-row-${u.email}`}>
                        <td className="p-3 font-mono text-xs truncate max-w-[220px]">
                          <div className="truncate">{u.email}</div>
                          {u.name && <div className="text-[10px] text-muted-foreground truncate">{u.name}</div>}
                        </td>
                        <td className="p-3 font-mono text-xs text-muted-foreground hidden md:table-cell whitespace-nowrap">
                          {formatRelative(u.created_at)}
                        </td>
                        <td className="p-3 font-mono text-sm text-right">
                          <span className={u.summaries_count > 0 ? "text-foreground" : "text-muted-foreground"}>
                            {u.summaries_count}
                          </span>
                        </td>
                        <td className="p-3 font-mono text-xs text-muted-foreground hidden md:table-cell whitespace-nowrap">
                          {formatRelative(u.last_activity)}
                        </td>
                        <td className="p-3 text-center">
                          {u.is_pro ? (
                            <span className="inline-flex items-center gap-1 bg-[#002FA7] text-white px-2 py-0.5 font-mono uppercase tracking-[0.15em] text-[9px]">
                              <CheckCircle2 className="w-2.5 h-2.5" /> Pro
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-muted-foreground font-mono uppercase tracking-[0.15em] text-[9px]">
                              <XCircle className="w-2.5 h-2.5" /> Free
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <div className="inline-flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => handleResetPassword(u.email)}
                              data-testid={`admin-activity-reset-pw-${u.email}`}
                              className="font-mono uppercase tracking-[0.2em] text-[10px] border border-border px-2 py-1 hover:bg-[#FF2A00] hover:text-white transition-colors inline-flex items-center gap-1"
                              title="Réinitialiser le mot de passe"
                            >
                              <KeyRound className="w-3 h-3" />
                            </button>
                            {u.is_pro ? (
                              <button
                                type="button"
                                onClick={() => handleRevoke(u.email)}
                                disabled={isSelf}
                                data-testid={`admin-activity-revoke-${u.email}`}
                                className="font-mono uppercase tracking-[0.2em] text-[10px] border border-[#FF2A00] text-[#FF2A00] px-2 py-1 hover:bg-[#FF2A00] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1"
                                title={isSelf ? "Vous ne pouvez pas révoquer votre propre accès" : "Révoquer le Pro"}
                              >
                                <UserMinus className="w-3 h-3" /> Révoquer
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Marketing — Pro conversions & Top referrers */}
          <div className="mt-12 max-w-5xl" data-testid="admin-marketing-section">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="sux-marker mb-1 flex items-center gap-2">
                  <TrendingUp className="w-3 h-3" /> Marketing
                </div>
                <h3 className="text-xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
                  Conversions Pro
                </h3>
              </div>
              <button
                type="button"
                onClick={loadMarketing}
                disabled={marketingLoading}
                data-testid="admin-marketing-refresh-btn"
                className="font-mono uppercase tracking-[0.2em] text-[10px] border border-border px-3 py-2 hover:bg-[#FF2A00] hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {marketingLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Rafraîchir
              </button>
            </div>

            {/* Conversion cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-0 border border-border" data-testid="admin-marketing-cards">
              <div className="p-5 border-r border-b md:border-b-0 border-border">
                <div className="sux-marker mb-1 flex items-center gap-1">
                  <Users className="w-2.5 h-2.5" /> Conversion
                </div>
                <div className="text-2xl font-black text-[#002FA7]" style={{ fontFamily: "Outfit" }}>
                  {marketing ? `${(marketing.overall_conversion_rate * 100).toFixed(1)}%` : "—"}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  {marketing ? `${marketing.total_pro}/${marketing.total_users} Pro` : "chargement…"}
                </div>
              </div>
              <div className="p-5 md:border-r border-b md:border-b-0 border-border">
                <div className="sux-marker mb-1 flex items-center gap-1">
                  <CreditCard className="w-2.5 h-2.5" /> Payés
                </div>
                <div className="text-2xl font-black" style={{ fontFamily: "Outfit" }}>
                  {marketing ? marketing.pro_via_payment : "—"}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">Stripe direct</div>
              </div>
              <div className="p-5 col-span-2 md:col-span-1">
                <div className="sux-marker mb-1">7 j / 30 j</div>
                <div className="text-2xl font-black" style={{ fontFamily: "Outfit" }}>
                  {marketing ? `${marketing.pro_last_7d} / ${marketing.pro_last_30d}` : "—"}
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">Nouveaux Pro</div>
              </div>
            </div>
          </div>

          {/* Pro users summary list (kept for quick view) */}
          <div className="mt-10 max-w-3xl">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="sux-marker mb-1">Utilisateurs Pro</div>
                <h3 className="text-xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
                  {proUsers.length} compte{proUsers.length > 1 ? "s" : ""} Pro
                </h3>
              </div>              <button
                type="button"
                onClick={loadProUsers}
                disabled={listLoading}
                data-testid="admin-refresh-btn"
                className="font-mono uppercase tracking-[0.2em] text-[10px] border border-border px-3 py-2 hover:bg-[#FF2A00] hover:text-white transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {listLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                Rafraîchir
              </button>
            </div>

            <div className="border border-border" data-testid="admin-pro-list">
              {proUsers.length === 0 && !listLoading && (
                <div className="p-6 font-mono text-sm text-muted-foreground text-center">
                  Aucun utilisateur Pro pour le moment.
                </div>
              )}
              {proUsers.map((u, idx) => (
                <div
                  key={u.email}
                  className={`flex items-center justify-between gap-4 p-4 ${idx > 0 ? "border-t border-border" : ""}`}
                  data-testid={`admin-pro-row-${u.email}`}
                >
                  <div className="min-w-0">
                    <div className="font-mono text-sm truncate">{u.email}</div>
                    {u.name && (
                      <div className="text-xs text-muted-foreground truncate">{u.name}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRevoke(u.email)}
                    disabled={u.email.toLowerCase() === user.email.toLowerCase()}
                    data-testid={`admin-revoke-btn-${u.email}`}
                    className="font-mono uppercase tracking-[0.2em] text-[10px] border border-[#FF2A00] text-[#FF2A00] px-3 py-2 hover:bg-[#FF2A00] hover:text-white transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                    title={u.email.toLowerCase() === user.email.toLowerCase() ? "Vous ne pouvez pas révoquer votre propre accès" : "Révoquer le Pro"}
                  >
                    <UserMinus className="w-3 h-3" /> Révoquer
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
